import SuggestionProvider from '../../types/SuggestionProvider';
import { getCategorySuggestions, getMasterCategoryList } from 'owa-categories';
import {
    createClientSuggestionInstrumentationContext,
    getLocalContentInstrumentationType,
} from './logClientSuggestions';
import {
    CategorySearchSuggestion,
    LocalContentInstrumentationContext,
    Suggestion,
    SuggestionKind,
} from 'owa-search-service';
import { MAX_CATEGORY_SUGGESTIONS_TO_SHOW, QUERY_LENGTH_FOR_CONFIDENCE } from '../../constants';

export type ClientSuggestions = {
    suggestions: Suggestion[];
    instrumentationContext: { [providerName: string]: LocalContentInstrumentationContext[] };
};

// Gets client suggestions using client suggestion providers.
export default function getClientSuggestions(queryStringForSuggestions: string): ClientSuggestions {
    const clientSuggestionProviders = getClientSuggestionProviders();
    const clientSuggestions: Suggestion[] = [];
    const clientSuggestionsInstrumentationContext: {
        [providerName: string]: LocalContentInstrumentationContext[];
    } = {};

    const trimmedQuery = queryStringForSuggestions.trim();
    for (const clientSuggestionProvider of clientSuggestionProviders) {
        const currentProviderSuggestions = clientSuggestionProvider.getSuggestions(
            trimmedQuery,
            clientSuggestionProvider.maxSuggestionsToGet
        );

        clientSuggestions.push(...currentProviderSuggestions);

        if (currentProviderSuggestions.length > 0) {
            const contentType = getLocalContentInstrumentationType(
                clientSuggestionProvider.suggestionKind
            );
            const currentProviderInstrumentationContext = createClientSuggestionInstrumentationContext(
                currentProviderSuggestions,
                contentType
            );

            clientSuggestionsInstrumentationContext[
                clientSuggestionProvider.providerName
            ] = currentProviderInstrumentationContext;
        }
    }

    return {
        suggestions: clientSuggestions,
        instrumentationContext: clientSuggestionsInstrumentationContext,
    };
}

/**
 * Gets client suggestion providers for suggestion types that are enabled. Currently,
 * category suggestion provider is available, if respective flight is enabled.
 */
function getClientSuggestionProviders() {
    const suggestionProviders: SuggestionProvider[] = [
        {
            maxSuggestionsToGet: MAX_CATEGORY_SUGGESTIONS_TO_SHOW,
            suggestionKind: SuggestionKind.Category,
            getSuggestions: getCategorySearchSuggestions,
            providerName: 'categoryprovider',
        },
    ];

    return suggestionProviders;
}

/**
 * Gets category suggestions for the given query text.
 *
 * @param queryStringForSuggestions The query text for which to get the suggestions
 * @param maxResults Number of suggestions to get
 * @return Suggestion collection
 */
function getCategorySearchSuggestions(
    queryStringForSuggestions: string,
    maxResults: number
): Suggestion[] {
    const categorySuggestions = getCategorySuggestions(
        queryStringForSuggestions,
        getMasterCategoryList()
    );
    const categorySuggestionsToShow: CategorySearchSuggestion[] = [];

    /**
     * Iterate over available categories until list is exhausted, or until we've
     * already matched the max number of maxResults to show.
     */
    for (
        let i = 0;
        i < categorySuggestions.suggestions.length && categorySuggestionsToShow.length < maxResults;
        i++
    ) {
        const suggestion = categorySuggestions.suggestions[i];

        /**
         * Push category suggestion only if either condition is true:
         *      1. The query text length is more than or equal to confidence
         *          threshold
         *      2. The query text length is same as the category name's which
         *          means we found the exact match (i.e. category name length
         *          is smaller than confidence threshold)
         */
        if (
            queryStringForSuggestions.length >= QUERY_LENGTH_FOR_CONFIDENCE ||
            queryStringForSuggestions.length == suggestion.Name.length
        ) {
            categorySuggestionsToShow.push({
                kind: SuggestionKind.Category,
                Name: suggestion.Name,
                ReferenceId: suggestion.Id,
            });
        }
    }

    return categorySuggestionsToShow;
}
